// Small pieces the Files app's screens share: a file's icon, its thumbnail,
// the presigned thumbnails of a screenful, downloads, and the upload tray.

import { useMemo } from 'react'
import {
  File as FileIcon,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileJson,
  FileSpreadsheet,
  FileText,
  FileType,
  FileVideo,
  Folder,
  X,
} from 'lucide-react'
import type { FileResource } from '@/kinds/file/apply'
import { fileUrl, fileUrls } from '@/lib/files/api'
import { viewerFor, formatSize, type ViewerKind } from '@/lib/files/types'
import { cancelUpload, useUploads } from '@/lib/files/uploads'
import { useToasts } from '@/state/toasts'
import { IconButton, cx } from '@/components/ui'
import { useAsync } from './useAsync'

const ICONS: Record<ViewerKind, typeof FileIcon> = {
  image: FileImage,
  svg: FileImage,
  video: FileVideo,
  audio: FileAudio,
  pdf: FileText,
  markdown: FileText,
  json: FileJson,
  csv: FileSpreadsheet,
  code: FileCode,
  text: FileText,
  zip: FileArchive,
  font: FileType,
  none: FileIcon,
}

export const KindIcon = ({ file, size = 20 }: { file: FileResource; size?: number }) => {
  if (file.spec.folder) return <Folder size={size} strokeWidth={1.75} className="text-accent" aria-hidden />
  const Icon = ICONS[viewerFor(file.spec.name, file.spec.content_type)]
  return <Icon size={size} strokeWidth={1.75} className="text-muted" aria-hidden />
}

/** Presigned thumbnails for the files on screen that have one (one call, cached). */
export const useThumbs = (files: readonly FileResource[]): Record<string, string> => {
  const ids = useMemo(
    () =>
      files
        .filter((f) => f.spec.thumb_key && f.status.state === 'ready')
        .map((f) => f.id)
        .slice(0, 200),
    [files],
  )
  const key = ids.length ? ids.join(',') : null
  const res = useAsync(key, () => fileUrls(ids, 'thumb'))
  return res.data ?? {}
}

/** A thumbnail, or the type's icon when there is none (or it will not draw). */
export const Thumb = ({
  file,
  url,
  className,
  icon = 20,
}: {
  file: FileResource
  url?: string
  className?: string
  icon?: number
}) =>
  url ? (
    <img
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      className={cx('object-cover bg-raised', className)}
      onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
    />
  ) : (
    <span className={cx('flex items-center justify-center bg-raised', className)}>
      <KindIcon file={file} size={icon} />
    </span>
  )

/** Download a file: a fresh five-minute URL whose answer is an attachment under the file's name. */
export const downloadFile = async (id: string): Promise<void> => {
  try {
    const url = await fileUrl(id, 'download')
    if (!url) throw new Error('the file is not ready')
    window.location.assign(url)
  } catch (e) {
    useToasts.getState().show(`Could not download: ${(e as Error).message}`)
  }
}

/** What is being sent, above the tab bar: each upload's progress and a way to stop it. */
export const UploadTray = () => {
  const uploads = useUploads((s) => s.uploads)
  const clear = useUploads((s) => s.clearDone)
  if (!uploads.length) return null
  const active = uploads.filter((u) => u.state === 'hashing' || u.state === 'sending')
  return (
    <div
      className="fixed z-30 inset-x-3 md:left-auto md:right-6 md:w-96 bottom-[calc(var(--above-tabbar)+0.75rem)] md:bottom-6 card bg-panel px-3 py-2.5 fade-in"
      role="status"
      aria-live="polite"
      data-testid="upload-tray"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[13px] font-semibold text-muted">
          {active.length ? `Uploading ${active.length}` : 'Uploads'}
        </span>
        {!active.length && (
          <IconButton label="Close" onClick={clear} className="min-h-8 min-w-8">
            <X size={16} />
          </IconButton>
        )}
      </div>
      <ul className="max-h-48 overflow-y-auto flex flex-col gap-2">
        {uploads.map((u) => {
          const pct = u.size ? Math.min(100, Math.round((u.sent / u.size) * 100)) : 100
          const live = u.state === 'hashing' || u.state === 'sending'
          return (
            <li key={u.id} className="text-[13px]" data-testid="upload-row">
              <div className="flex items-center gap-2">
                <span className="flex-1 min-w-0 truncate">{u.name}</span>
                <span className="text-xs text-faint shrink-0 tabular">
                  {u.state === 'hashing'
                    ? 'Checking'
                    : u.state === 'sending'
                      ? `${pct}%`
                      : u.state === 'done'
                        ? u.reused
                          ? 'Already here'
                          : formatSize(u.size)
                        : u.state === 'cancelled'
                          ? 'Stopped'
                          : 'Failed'}
                </span>
                {live && (
                  <IconButton
                    label={`Stop ${u.name}`}
                    className="min-h-7 min-w-7"
                    onClick={() => void cancelUpload(u.id)}
                  >
                    <X size={14} />
                  </IconButton>
                )}
              </div>
              {live && (
                <div className="h-1 mt-1 rounded-full bg-raised overflow-hidden">
                  <div
                    className="h-full bg-accent transition-[width] duration-200"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
              {u.error && <p className="text-xs text-faint mt-0.5">{u.error}</p>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
